import React from 'react'
import Fuse from 'fuse.js'
import { compose } from 'redux'
import { connect } from 'react-redux'

import { GUEST, MEMBER } from 'data/users/roles'
import { selectors as UsersSelectors } from 'data/users'
import { selectors as ConfigSelectors } from 'data/config'
import { selectors as UserGroupSelectors } from 'data/userGroups'

import SearcheableList from 'components/Utils/SearcheableList'
import { providesUserGroups } from 'components/Organizations/dataSync/userGroups'

import InviteRow from './InviteRow'
import MembershipRow from './MembershipRow'

function ChannelMembershipPicker({
  userGroupsList,
  users,
  blacklistedIds,
  userGroups,
  onSelection,
  channel,
  canInviteGuests,
  ...props
}) {
  const userGroupsArray = userGroupsList ? userGroupsList.toArray() : []
  const items = [
    ...userGroupsArray.map(userGroup => ({
      type: 'UserGroup',
      id: userGroup.id,
      userGroup,
    })),
    ...users.toArray().map(user => ({ type: 'User', id: user.id, user })),
  ].filter(({ id }) => !blacklistedIds.includes(id))

  return (
    <SearcheableList
      items={items}
      renderRow={({ item, selectionCallback }) => {
        if (item.type === 'invite-as-guest') {
          return (
            <InviteRow
              email={item.email}
              channel={channel}
              organizationRole={GUEST}
              onInvite={() => selectionCallback()}
            />
          )
        }
        if (item.type === 'invite-as-member') {
          return (
            <InviteRow
              email={item.email}
              channel={channel}
              organizationRole={MEMBER}
              onInvite={() => selectionCallback()}
            />
          )
        }
        return (
          <MembershipRow
            key={item.id}
            item={item}
            onSelection={item => {
              onSelection(item)
              selectionCallback()
            }}
          />
        )
      }}
      itemsContainerProps={{
        className:
          'color-mode-elevated position-absolute background-shade-100 width-full shadow-hovering scroll-y radius-normal',
        style: {
          maxHeight: 190,
        },
      }}
      showItemsOnEmptyTerm={false}
      filterItems={(items, term) => {
        if (!term) {
          return []
        }

        const fuse = new Fuse(items, {
          shouldSort: true,
          threshold: 0.3,
          location: 0,
          distance: 100,
          maxPatternLength: 32,
          minMatchCharLength: 1,
          keys: ['user.username', 'user.displayName', 'userGroup.name'],
        })
        const itemsFromSearch = fuse.search(term)

        if (itemsFromSearch.length) {
          return itemsFromSearch
        }

        if (term.length > 2) {
          const inviteItems = [
            {
              type: 'invite-as-member',
              email: term,
            },
          ]

          if (canInviteGuests) {
            inviteItems.push({
              type: 'invite-as-guest',
              email: term,
            })
          }

          return inviteItems
        }

        return []
      }}
      {...props}
    />
  )
}

export default compose(
  providesUserGroups(),
  connect(state => ({
    canInviteGuests: ConfigSelectors.hasFlag(state, 'guest-role'),
    users: UsersSelectors.getAllUsers(state, { filterOutArchived: true }),
    userGroupsList: UserGroupSelectors.listOrganizationUserGroups(state),
  }))
)(ChannelMembershipPicker)
